/**
 * Configuration file for NorthBuilt Compass Chat Interface
 *
 * SETUP INSTRUCTIONS:
 *
 * For Local Development:
 *   1. Set VITE_REST_API_ENDPOINT to local server URL (optional)
 *   2. Set VITE_DEV_MODE to 'true' to bypass authentication
 *   3. Start web server: npm run dev
 *   4. Open: http://localhost:5173
 *
 * For Production:
 *   1. Environment variables injected during CI/CD build via Terraform outputs
 *   2. Or use hardcoded fallback values (already configured)
 *   3. Build: npm run build
 *   4. Deploy dist/ to S3 + CloudFront via CI/CD
 */

import type { Config } from './types';

const CONFIG: Config = {
  // ============================================================
  // API Configuration (REST API Gateway)
  // ============================================================
  // REST API Gateway endpoint for chat (SSE streaming responses)
  // LOCAL:      http://localhost:3000 (set VITE_REST_API_ENDPOINT)
  // PRODUCTION: https://h7o1tv7py5.execute-api.us-east-1.amazonaws.com/v1
  REST_API_ENDPOINT:
    import.meta.env.VITE_REST_API_ENDPOINT ||
    'https://h7o1tv7py5.execute-api.us-east-1.amazonaws.com/v1',

  // ============================================================
  // AWS Cognito Configuration (for Authentication)
  // ============================================================
  // Get these values from: terraform output
  COGNITO_DOMAIN:
    import.meta.env.VITE_COGNITO_DOMAIN ||
    'nb-rag-sys-auth.auth.us-east-1.amazoncognito.com',
  COGNITO_CLIENT_ID:
    import.meta.env.VITE_COGNITO_CLIENT_ID || '3s4ujksv7a82k58j06sevvtnc5',
  COGNITO_REDIRECT_URI:
    import.meta.env.VITE_COGNITO_REDIRECT_URI ||
    `${window.location.origin}/callback.html`,
  COGNITO_LOGOUT_URI:
    import.meta.env.VITE_COGNITO_LOGOUT_URI || window.location.origin,

  // ============================================================
  // Chat Configuration
  // ============================================================
  CONVERSATION_HISTORY_LIMIT:
    Number(import.meta.env.VITE_CONVERSATION_HISTORY_LIMIT) || 10, // Number of messages to display
  MAX_RESULTS: Number(import.meta.env.VITE_MAX_RESULTS) || 5, // Number of source documents to retrieve
  MAX_MESSAGE_LENGTH: Number(import.meta.env.VITE_MAX_MESSAGE_LENGTH) || 4000, // Max characters per message

  // ============================================================
  // External Links
  // ============================================================
  DOCS_URL:
    import.meta.env.VITE_DOCS_URL || 'https://docs.compass.northbuilt.com',

  // ============================================================
  // Development Settings
  // ============================================================
  // DEV_MODE: Bypass authentication for quick local testing
  //   - true:  Skip login (use for quick UI testing)
  //   - false: Use Cognito OAuth (recommended for full testing)
  DEV_MODE: import.meta.env.VITE_DEV_MODE === 'true' || false,

  // DEBUG: Enable detailed console logging
  // Set VITE_DEBUG=true in .env for development debugging
  DEBUG: import.meta.env.VITE_DEBUG === 'true',
};

// Log configuration on load (only in debug mode)
if (CONFIG.DEBUG) {
  console.log('Configuration loaded:', {
    hostname: window.location.hostname,
    devMode: CONFIG.DEV_MODE,
    restApiEndpoint: CONFIG.REST_API_ENDPOINT,
    cognitoDomain: CONFIG.COGNITO_DOMAIN,
  });
}

export default CONFIG;
